import { useCallback, useEffect, useState } from "react";
import { API_BASE, fetchWithAuth } from "../utils/api";

const normalizeRole = (role) => {
  if (!role) return role;
  const roleLower = String(role).toLowerCase();
  if (roleLower === "dispatchers") return "Dispatchers";
  if (roleLower === "accounting") return "accounting";
  return role;
};

export function useAuth() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/auth/check-login`);
      if (!res.ok) {
        setUser(null);
        // Nếu response là 401 hoặc 403, redirect về login
        if (res.status === 401 || res.status === 403) {
          if (window.location.pathname.includes('/revenue')) {
            window.location.href = '/login';
          }
        }
        return;
      }
      const data = await res.json();

      const userRole = data?.user?.role?.toLowerCase();
      const allowedRoles = ["dispatchers", "accounting", "admin", "driver"];
      if (!allowedRoles.includes(userRole)) {
        setUser(null);
        // Nếu role không được phép, redirect về login
        if (window.location.pathname.includes('/revenue')) {
          window.location.href = '/login';
        }
        return;
      }

      setUser({
        ...data.user,
        role: normalizeRole(data.user.role),
      });
    } catch (err) {
      console.error("Lỗi khi lấy thông tin user:", err);
      setUser(null);
      // Nếu có lỗi network hoặc lỗi khác, kiểm tra xem có phải do hết phiên không
      if (window.location.pathname.includes('/revenue')) {
        // Có thể là lỗi network, nhưng để an toàn, nếu đang ở revenue app thì redirect
        // Chỉ redirect nếu không phải lỗi network thông thường
        if (err.message && !err.message.includes('Failed to fetch')) {
          window.location.href = '/login';
        }
      }
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        await loadUser();
      } finally {
        setAuthLoading(false);
      }
    };
    run();

    // Kiểm tra định kỳ mỗi 5 phút để đảm bảo phiên đăng nhập vẫn còn hiệu lực
    const interval = setInterval(async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/auth/check-login`);
        if (!res.ok || res.status === 401 || res.status === 403) {
          // Phiên đã hết hạn, redirect về login
          if (window.location.pathname.includes('/revenue')) {
            window.location.href = '/login';
          }
        } else {
          const data = await res.json();
          const userRole = data?.user?.role?.toLowerCase();
          const allowedRoles = ["dispatchers", "accounting", "admin", "driver"];
          if (!allowedRoles.includes(userRole)) {
            // Role không được phép, redirect về login
            if (window.location.pathname.includes('/revenue')) {
              window.location.href = '/login';
            }
          }
        }
      } catch (err) {
        // Bỏ qua lỗi trong kiểm tra định kỳ để tránh spam
        console.warn("Lỗi khi kiểm tra phiên đăng nhập:", err);
      }
    }, 5 * 60 * 1000); // 5 phút

    return () => clearInterval(interval);
  }, [loadUser]);

  const logout = useCallback(async () => {
    try {
      await fetchWithAuth(`${API_BASE}/auth/logout`, { method: "POST" });
    } catch (err) {
      console.error("Lỗi khi đăng xuất:", err);
    } finally {
      setUser(null);
    }
  }, []);

  return { user, setUser, authLoading, reloadUser: loadUser, logout };
}

